import { domainInputFields, isDomainInputVisible, parseDomainInputNumber } from './fieldInputContract.js';
import { isAssessmentDomain, getDomainNumericMaximum } from './recordingContract.js';
import { hasRecordInputSchema, recordInputDomain, recordFieldMaximum } from './recordInputSchema.js';
import { recordFieldValue } from './recordFieldValue.js';

export function numericWeightError(field) {
  if (field.weight === undefined) return null;
  if (field.type !== 'number' || typeof field.weight !== 'number'
    || !Number.isFinite(field.weight) || field.weight < 0) return 'requires a non-negative Number contribution';
  if (field.weight > 0 && !(typeof field.maxScore === 'number' && Number.isFinite(field.maxScore) && field.maxScore > 0)) {
    return 'needs a maximum value to calculate a percentage';
  }
  return null;
}

/** A number is a contribution only when the school explicitly gives it a weight. */
export function numericTotalFields(domain) {
  if (domain?.runtime?.protected === true || isAssessmentDomain(domain)
    || domain?.listBlocks?.weighting === false) return [];
  const fields = domainInputFields(domain).filter(field => field.type === 'number'
    && typeof field.weight === 'number' && Number.isFinite(field.weight) && field.weight > 0);
  return fields.length >= 2 ? fields : [];
}

/** Missing contributions never silently become zero or a different denominator. */
export function numericInputTotal(domain, values, { maximum, maximumFor } = {}) {
  const fields = numericTotalFields(domain);
  if (!fields.length) return null;
  const allFields = domainInputFields(domain);
  const contributions = fields.map(field => {
    const value = parseDomainInputNumber(values?.[field.id]);
    const max = maximumFor ? maximumFor(field) : field === domain.primary
      ? getDomainNumericMaximum(domain, maximum) : field.maxScore;
    const valid = isDomainInputVisible(field, values, allFields) && value != null
      && typeof max === 'number' && Number.isFinite(max) && max > 0 && value <= max;
    return { fieldId: field.id, weight: field.weight, percent: valid ? value / max * 100 : null };
  });
  const missing = contributions.filter(item => item.percent == null).length;
  const weight = contributions.reduce((sum, item) => sum + item.weight, 0);
  return { complete: missing === 0, missing, contributions,
    value: missing ? null : contributions.reduce((sum, item) => sum + item.percent * item.weight, 0) / weight };
}

/** A historical total uses its saved contributors, weights and denominators. */
export function numericRecordTotal(record, domain) {
  if (!hasRecordInputSchema(record)) return null;
  const saved = recordInputDomain(record, domain);
  const values = Object.fromEntries(domainInputFields(saved)
    .map(field => [field.id, recordFieldValue(record, field.id, saved)]));
  return numericInputTotal(saved, values, { maximumFor: field => recordFieldMaximum(record, field.id, saved) });
}

export function numericTotalText(total) {
  if (!total) return null;
  return total.complete ? `${Math.round(total.value * 10) / 10}%`
    : `Incomplete · ${total.missing} ${total.missing === 1 ? 'answer' : 'answers'} missing`;
}

/** One form can apply shared details to several learners' distinct inputs. */
export function numericFormTotals(domain, values, { contexts, labels, includePrimary = true, maximum } = {}) {
  const rows = !includePrimary && contexts?.length ? contexts : [{}];
  return rows.flatMap((context, index) => {
    const total = numericInputTotal(domain, { ...context, ...values }, { maximum });
    return total ? [{ label: labels?.[index] || 'Combined total', total, text: numericTotalText(total) }] : [];
  });
}
